import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EnvelopeSimple, InstagramLogo, MapPin, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Reveal, ChapterLabel } from "./Reveal";
import { site } from "../content";

const inputClass =
  "w-full rounded-xl bg-[#FBF8F4]/[0.06] border border-[#FBF8F4]/15 px-4 py-3.5 text-[#FBF8F4] placeholder:text-[#FBF8F4]/35 focus:border-[#7C8C7E] focus:bg-[#FBF8F4]/10 outline-none transition-[border-color,background-color] duration-200";

const labelClass = "block text-sm font-medium text-[#FBF8F4]/70 mb-2";
const errorClass = "mt-2 text-sm text-[#E0A58F]";
const topicSlugs = {
  Kuvaus: "kuvaus",
  Editointi: "editointi",
  Sisältösuunnittelu: "sisaltosuunnittelu",
  Sparraus: "sparraus",
  "En ole varma": "en-ole-varma",
};

export const Contact = () => {
  const { contact } = site;
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [topics, setTopics] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const toggleTopic = (t) =>
    setTopics((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t]));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = contact.errors.name;
    if (!form.email.trim()) e.email = contact.errors.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = contact.errors.emailInvalid;
    if (!form.message.trim()) e.message = contact.errors.message;
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim() || null,
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          topics,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setForm({ name: "", company: "", email: "", phone: "", message: "" });
    setTopics([]);
    setErrors({});
    setStatus("idle");
  };

  return (
    <section id="yhteys" className="px-4 md:px-6 pb-6" data-testid="contact-section">
      <div className="grain max-w-7xl mx-auto rounded-[2.5rem] bg-[#2B2724] text-[#FBF8F4] px-6 py-20 md:p-20 overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 relative z-[1]">
          <div className="lg:col-span-5">
            <Reveal>
              <ChapterLabel chapter={contact.chapter} label={contact.label} dark />
              <h2 className="mt-5 font-serif font-light text-3xl md:text-5xl tracking-tight leading-tight">
                {contact.title}
              </h2>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-[#FBF8F4]/70">{contact.lead}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12">
                <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#7C8C7E]">
                  {contact.directTitle}
                </h3>
                <ul className="mt-6 space-y-5">
                  <li>
                    <a
                      href={`mailto:${site.email}`}
                      className="group flex items-center gap-4 text-[#FBF8F4]/85 hover:text-[#FBF8F4] transition-colors duration-200"
                      data-testid="contact-info-email"
                    >
                      <span className="w-11 h-11 rounded-full bg-[#FBF8F4]/[0.07] flex items-center justify-center group-hover:bg-[#7C8C7E] transition-colors duration-300">
                        <EnvelopeSimple size={20} />
                      </span>
                      {site.email}
                    </a>
                  </li>
                  <li>
                    <a
                      href={site.instagram.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-4 text-[#FBF8F4]/85 hover:text-[#FBF8F4] transition-colors duration-200"
                      data-testid="contact-info-instagram"
                    >
                      <span className="w-11 h-11 rounded-full bg-[#FBF8F4]/[0.07] flex items-center justify-center group-hover:bg-[#7C8C7E] transition-colors duration-300">
                        <InstagramLogo size={20} />
                      </span>
                      {site.instagram.handle}
                    </a>
                  </li>
                  <li className="flex items-center gap-4 text-[#FBF8F4]/60" data-testid="contact-info-area">
                    <span className="w-11 h-11 rounded-full bg-[#FBF8F4]/[0.07] flex items-center justify-center shrink-0">
                      <MapPin size={20} />
                    </span>
                    <span className="text-sm leading-relaxed">{site.area}</span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="h-full min-h-[24rem] rounded-3xl bg-[#FBF8F4]/[0.06] border border-[#FBF8F4]/10 flex flex-col items-center justify-center text-center p-10"
                  data-testid="contact-success-message"
                >
                  <CheckCircle size={52} weight="light" className="text-[#7C8C7E]" />
                  <h3 className="mt-6 font-serif text-3xl">{contact.successTitle}</h3>
                  <p className="mt-3 text-[#FBF8F4]/70 max-w-sm">{contact.successText}</p>
                  <button
                    onClick={reset}
                    className="mt-8 rounded-full border border-[#FBF8F4]/30 px-7 py-3 text-sm font-medium hover:bg-[#FBF8F4] hover:text-[#2B2724] transition-colors duration-200"
                    data-testid="contact-send-another-button"
                  >
                    {contact.successAgain}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  onSubmit={onSubmit}
                  noValidate
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-name" className={labelClass}>
                        {contact.fields.name} <span aria-hidden="true" className="text-[#7C8C7E]">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder={contact.placeholders.name}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "contact-name-error" : undefined}
                        className={inputClass}
                        data-testid="contact-name-input"
                      />
                      {errors.name && (
                        <p id="contact-name-error" role="alert" className={errorClass} data-testid="contact-name-error">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="contact-company" className={labelClass}>
                        {contact.fields.company}
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        value={form.company}
                        onChange={set("company")}
                        placeholder={contact.placeholders.company}
                        className={inputClass}
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className={labelClass}>
                        {contact.fields.email} <span aria-hidden="true" className="text-[#7C8C7E]">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder={contact.placeholders.email}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "contact-email-error" : undefined}
                        className={inputClass}
                        data-testid="contact-email-input"
                      />
                      {errors.email && (
                        <p id="contact-email-error" role="alert" className={errorClass} data-testid="contact-email-error">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className={labelClass}>
                        {contact.fields.phone}
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder={contact.placeholders.phone}
                        className={inputClass}
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <fieldset>
                    <legend className={labelClass}>{contact.fields.topics}</legend>
                    <div className="flex flex-wrap gap-2.5">
                      {contact.topicOptions.map((t) => {
                        const active = topics.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => toggleTopic(t)}
                            aria-pressed={active}
                            className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors duration-200 ${
                              active
                                ? "bg-[#7C8C7E] border-[#7C8C7E] text-[#FBF8F4]"
                                : "border-[#FBF8F4]/20 text-[#FBF8F4]/70 hover:border-[#7C8C7E] hover:text-[#FBF8F4]"
                            }`}
                            data-testid={`contact-topic-${topicSlugs[t]}`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <div>
                    <label htmlFor="contact-message" className={labelClass}>
                      {contact.fields.message} <span aria-hidden="true" className="text-[#7C8C7E]">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      placeholder={contact.placeholders.message}
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? "contact-message-error" : undefined}
                      className={`${inputClass} resize-y min-h-[8rem]`}
                      data-testid="contact-message-input"
                    />
                    {errors.message && (
                      <p id="contact-message-error" role="alert" className={errorClass} data-testid="contact-message-error">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {status === "error" && (
                    <p role="alert" className={errorClass} data-testid="contact-error-message">
                      {contact.errors.sendFailed}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="rounded-full bg-[#7C8C7E] text-[#FBF8F4] font-medium px-9 py-4 flex items-center gap-2.5 hover:bg-[#5E6D60] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                    data-testid="contact-submit-button"
                  >
                    {status === "sending" ? (
                      <>
                        <CircleNotch size={18} className="animate-spin" />
                        {contact.sending}
                      </>
                    ) : (
                      contact.submit
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
